import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  FaGraduationCap, FaChevronDown, FaBookOpen, FaBook, FaGlobe, FaBrain, 
  FaCheck, FaStar, FaLightbulb, FaComments, FaLeaf, FaHandsHelping, 
  FaRobot, FaUsers, FaQuestion, FaCalendar, FaChalkboardTeacher, 
  FaUserTie, FaEnvelope, FaCrown, FaBalanceScale, FaUser, FaSignInAlt, 
  FaUserPlus, FaTachometerAlt, FaBars, FaTimes, FaSignOutAlt
} from 'react-icons/fa';

const MegaMenu: React.FC = () => {
  const [activeMegaMenu, setActiveMegaMenu] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();

  // Handle body overflow when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  return (
    <div className="fixed top-0 left-0 right-0 bg-white shadow-lg border-b border-gray-200 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 rtl:space-x-reverse">
            <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
              <span className="text-white text-xl font-bold">و</span>
            </div>
            <div className="hidden sm:block">
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">پیروز Pirooz</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">Farsi Vocabulary Builder</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex lg:items-center lg:space-x-6">
            {/* Learn Menu */}
            <div className="relative group">
              <button
                className="flex items-center px-4 py-2 text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200"
                onMouseEnter={() => setActiveMegaMenu('learn')}
                onMouseLeave={() => setActiveMegaMenu(null)}
              >
                <FaGraduationCap className="mr-2 text-blue-600" />
                Learn
                <FaChevronDown className="ml-1 w-4 h-4" />
              </button>
              
              {/* Learn Mega Menu */}
              {activeMegaMenu === 'learn' && (
                <div 
                  className="absolute top-full left-1/2 transform -translate-x-1/2 w-screen max-w-4xl bg-white shadow-2xl border-t-4 border-blue-600 rounded-b-lg z-50"
                  onMouseEnter={() => setActiveMegaMenu('learn')}
                  onMouseLeave={() => setActiveMegaMenu(null)}
                >
                  <div className="grid grid-cols-3 gap-8 p-8">
                    {/* Courses Section */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <FaBookOpen className="mr-2 text-blue-600" />
                        Courses & Pathways
                      </h3>
                      <ul className="space-y-3">
                        <li>
                          <Link to="/alphabet-learning" className="flex items-center text-gray-700 hover:text-blue-600 transition-colors">
                            <FaBook className="mr-2 text-orange-500" />
                            Alphabet Learning
                          </Link>
                        </li>
                        <li>
                          <Link to="/pathways" className="flex items-center text-gray-700 hover:text-blue-600 transition-colors">
                            <FaGraduationCap className="mr-2 text-green-500" />
                            All Courses
                          </Link>
                        </li>
                        <li>
                          <Link to="/cultural" className="flex items-center text-gray-700 hover:text-blue-600 transition-colors">
                            <FaGlobe className="mr-2 text-purple-500" />
                            Cultural Lessons
                          </Link>
                        </li>
                        <li>
                          <Link to="/hafez" className="flex items-center text-gray-700 hover:text-blue-600 transition-colors">
                            <FaBookOpen className="mr-2 text-indigo-500" />
                            Hafez Poetry
                          </Link>
                        </li>
                      </ul>
                    </div>

                    {/* Practice Section */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <FaBrain className="mr-2 text-blue-600" />
                        Practice & Tools
                      </h3>
                      <ul className="space-y-3">
                        <li>
                          <Link to="/flashcards" className="flex items-center text-gray-700 hover:text-blue-600 transition-colors">
                            <FaBook className="mr-2 text-yellow-500" />
                            Flashcards
                          </Link>
                        </li>
                        <li>
                          <Link to="/quiz" className="flex items-center text-gray-700 hover:text-blue-600 transition-colors">
                            <FaCheck className="mr-2 text-green-500" />
                            Quizzes
                          </Link>
                        </li>
                        <li>
                          <Link to="/progress" className="flex items-center text-gray-700 hover:text-blue-600 transition-colors">
                            <FaStar className="mr-2 text-blue-500" />
                            Progress Tracker
                          </Link>
                        </li>
                        <li>
                          <Link to="/word-of-the-day" className="flex items-center text-gray-700 hover:text-blue-600 transition-colors">
                            <FaLightbulb className="mr-2 text-orange-500" />
                            Word of the Day
                          </Link>
                        </li>
                      </ul>
                    </div>

                    {/* Resources Section */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <FaBook className="mr-2 text-blue-600" />
                        Resources
                      </h3>
                      <ul className="space-y-3">
                        <li>
                          <Link to="/idioms" className="flex items-center text-gray-700 hover:text-blue-600 transition-colors">
                            <FaComments className="mr-2 text-red-500" />
                            Persian Idioms
                          </Link>
                        </li>
                        <li>
                          <Link to="/vocabulary-garden" className="flex items-center text-gray-700 hover:text-blue-600 transition-colors">
                            <FaLeaf className="mr-2 text-green-500" />
                            Vocabulary Garden
                          </Link>
                        </li>
                        <li>
                          <Link to="/learning-guide" className="flex items-center text-gray-700 hover:text-blue-600 transition-colors">
                            <FaHandsHelping className="mr-2 text-purple-500" />
                            Learning Guide
                          </Link>
                        </li>
                        <li>
                          <Link to="/ai-chat" className="flex items-center text-gray-700 hover:text-blue-600 transition-colors">
                            <FaRobot className="mr-2 text-indigo-500" />
                            AI Chat Assistant
                          </Link>
                        </li>
                        <li>
                          <Link to="/blog" className="flex items-center text-gray-700 hover:text-blue-600 transition-colors">
                            <FaBookOpen className="mr-2 text-teal-500" />
                            Blog & Articles
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Community Menu */}
            <div className="relative group">
              <button
                className="flex items-center px-4 py-2 text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200"
                onMouseEnter={() => setActiveMegaMenu('community')}
                onMouseLeave={() => setActiveMegaMenu(null)}
              >
                <FaUsers className="mr-2 text-green-600" />
                Community
                <FaChevronDown className="ml-1 w-4 h-4" />
              </button>
              
              {/* Community Mega Menu */}
              {activeMegaMenu === 'community' && (
                <div 
                  className="absolute top-full left-1/2 transform -translate-x-1/2 w-screen max-w-4xl bg-white shadow-2xl border-t-4 border-green-600 rounded-b-lg z-50"
                  onMouseEnter={() => setActiveMegaMenu('community')}
                  onMouseLeave={() => setActiveMegaMenu(null)}
                >
                  <div className="grid grid-cols-3 gap-8 p-8">
                    {/* Connect Section */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <FaComments className="mr-2 text-green-600" />
                        Connect & Share
                      </h3>
                      <ul className="space-y-3">
                        <li>
                          <Link to="/forum" className="flex items-center text-gray-700 hover:text-green-600 transition-colors">
                            <FaComments className="mr-2 text-blue-500" />
                            Community Forum
                          </Link>
                        </li>
                        <li>
                          <Link to="/teacher-qa" className="flex items-center text-gray-700 hover:text-green-600 transition-colors">
                            <FaQuestion className="mr-2 text-orange-500" />
                            Teacher Q&A
                          </Link>
                        </li>
                        <li>
                          <Link to="/success-stories" className="flex items-center text-gray-700 hover:text-green-600 transition-colors">
                            <FaStar className="mr-2 text-yellow-500" />
                            Success Stories
                          </Link>
                        </li>
                        <li>
                          <Link to="/events" className="flex items-center text-gray-700 hover:text-green-600 transition-colors">
                            <FaCalendar className="mr-2 text-purple-500" />
                            Events & Meetups
                          </Link>
                        </li>
                      </ul>
                    </div>

                    {/* Teaching Section */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <FaChalkboardTeacher className="mr-2 text-green-600" />
                        Teaching
                      </h3>
                      <ul className="space-y-3">
                        <li>
                          <Link to="/become-teacher" className="flex items-center text-gray-700 hover:text-green-600 transition-colors">
                            <FaUserTie className="mr-2 text-indigo-500" />
                            Become a Teacher
                          </Link>
                        </li>
                        <li>
                          <Link to="/teachers" className="flex items-center text-gray-700 hover:text-green-600 transition-colors">
                            <FaChalkboardTeacher className="mr-2 text-green-500" />
                            Find Teachers
                          </Link>
                        </li>
                        <li>
                          <Link to="/blog" className="flex items-center text-gray-700 hover:text-green-600 transition-colors">
                            <FaBookOpen className="mr-2 text-blue-500" />
                            Teaching Blog
                          </Link>
                        </li>
                        <li>
                          <Link to="/resources" className="flex items-center text-gray-700 hover:text-green-600 transition-colors">
                            <FaHandsHelping className="mr-2 text-orange-500" />
                            Teaching Resources
                          </Link>
                        </li>
                      </ul>
                    </div>

                    {/* Support Section */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <FaHandsHelping className="mr-2 text-green-600" />
                        Support
                      </h3>
                      <ul className="space-y-3">
                        <li>
                          <Link to="/help" className="flex items-center text-gray-700 hover:text-green-600 transition-colors">
                            <FaQuestion className="mr-2 text-blue-500" />
                            Help Center
                          </Link>
                        </li>
                        <li>
                          <Link to="/faq" className="flex items-center text-gray-700 hover:text-green-600 transition-colors">
                            <FaComments className="mr-2 text-green-500" />
                            FAQ
                          </Link>
                        </li>
                        <li>
                          <Link to="/contact" className="flex items-center text-gray-700 hover:text-green-600 transition-colors">
                            <FaEnvelope className="mr-2 text-purple-500" />
                            Contact Us
                          </Link>
                        </li>
                        <li>
                          <Link to="/feedback" className="flex items-center text-gray-700 hover:text-green-600 transition-colors">
                            <FaLightbulb className="mr-2 text-yellow-500" />
                            Send Feedback
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Premium Menu */}
            <div className="relative group">
              <button
                className="flex items-center px-4 py-2 text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200"
                onMouseEnter={() => setActiveMegaMenu('premium')}
                onMouseLeave={() => setActiveMegaMenu(null)}
              >
                <FaCrown className="mr-2 text-yellow-600" />
                Premium
                <FaChevronDown className="ml-1 w-4 h-4" />
              </button>
              
              {/* Premium Mega Menu */}
              {activeMegaMenu === 'premium' && (
                <div 
                  className="absolute top-full left-1/2 transform -translate-x-1/2 w-screen max-w-4xl bg-white shadow-2xl border-t-4 border-yellow-600 rounded-b-lg z-50"
                  onMouseEnter={() => setActiveMegaMenu('premium')}
                  onMouseLeave={() => setActiveMegaMenu(null)}
                >
                  <div className="grid grid-cols-3 gap-8 p-8">
                    {/* Plans Section */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <FaCrown className="mr-2 text-yellow-600" />
                        Premium Plans
                      </h3>
                      <ul className="space-y-3">
                        <li>
                          <Link to="/pricing" className="flex items-center text-gray-700 hover:text-yellow-600 transition-colors">
                            <FaStar className="mr-2 text-yellow-500" />
                            View All Plans
                          </Link>
                        </li>
                        <li>
                          <Link to="/subscription" className="flex items-center text-gray-700 hover:text-yellow-600 transition-colors">
                            <FaCrown className="mr-2 text-purple-500" />
                            Manage Subscription
                          </Link>
                        </li>
                        <li>
                          <Link to="/features" className="flex items-center text-gray-700 hover:text-yellow-600 transition-colors">
                            <FaCheck className="mr-2 text-green-500" />
                            Premium Features
                          </Link>
                        </li>
                        <li>
                          <Link to="/comparison" className="flex items-center text-gray-700 hover:text-yellow-600 transition-colors">
                            <FaBalanceScale className="mr-2 text-blue-500" />
                            Platform Comparison
                          </Link>
                        </li>
                      </ul>
                    </div>

                    {/* Benefits Section */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <FaStar className="mr-2 text-yellow-600" />
                        Premium Benefits
                      </h3>
                      <ul className="space-y-3">
                        <li className="flex items-center text-gray-700">
                          <FaCheck className="mr-2 text-green-500" />
                          Unlimited Lessons
                        </li>
                        <li className="flex items-center text-gray-700">
                          <FaCheck className="mr-2 text-green-500" />
                          Live Q&A Sessions
                        </li>
                        <li className="flex items-center text-gray-700">
                          <FaCheck className="mr-2 text-green-500" />
                          Offline Access
                        </li>
                        <li className="flex items-center text-gray-700">
                          <FaCheck className="mr-2 text-green-500" />
                          Priority Support
                        </li>
                      </ul>
                    </div>

                    {/* Account Section */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <FaUser className="mr-2 text-yellow-600" />
                        Account
                      </h3>
                      <ul className="space-y-3">
                        {user ? (
                          <>
                            <li>
                              <Link to="/profile" className="flex items-center text-gray-700 hover:text-yellow-600 transition-colors">
                                <FaUser className="mr-2 text-purple-500" />
                                My Profile
                              </Link>
                            </li>
                            <li>
                              <Link to="/dashboard" className="flex items-center text-gray-700 hover:text-yellow-600 transition-colors">
                                <FaTachometerAlt className="mr-2 text-indigo-500" />
                                Dashboard
                              </Link>
                            </li>
                            <li>
                              <button 
                                onClick={logout}
                                className="flex items-center text-gray-700 hover:text-yellow-600 transition-colors w-full text-left"
                              >
                                <FaSignOutAlt className="mr-2 text-red-500" />
                                Log Out
                              </button>
                            </li>
                          </>
                        ) : (
                          <>
                            <li>
                              <Link to="/login" className="flex items-center text-gray-700 hover:text-yellow-600 transition-colors">
                                <FaSignInAlt className="mr-2 text-blue-500" />
                                Log In
                              </Link>
                            </li>
                            <li>
                              <Link to="/register" className="flex items-center text-gray-700 hover:text-yellow-600 transition-colors">
                                <FaUserPlus className="mr-2 text-green-500" />
                                Sign Up
                              </Link>
                            </li>
                          </>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Comparison Link */}
            <Link 
              to="/comparison" 
              className="flex items-center px-4 py-2 text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200"
            >
              <FaBalanceScale className="mr-2 text-blue-600" />
              Compare Platforms
            </Link>
          </div>

          {/* Mobile Menu Button & Auth */}
          <div className="flex items-center space-x-4">
            {/* Desktop Auth Buttons */}
            <div className="hidden lg:flex lg:items-center lg:space-x-4">
              {user ? (
                <>
                  <Link to="/profile" className="px-4 py-2 text-gray-700 hover:text-blue-600 font-medium transition-colors">
                    Profile
                  </Link>
                  <button 
                    onClick={logout}
                    className="px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Log out
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="px-4 py-2 text-gray-700 hover:text-blue-600 font-medium transition-colors">
                    Log in
                  </Link>
                  <Link to="/register" className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors">
                    Sign up
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Auth & Menu Button */}
            <div className="flex items-center space-x-2 lg:hidden">
              {user ? (
                <Link to="/profile" className="p-2 text-gray-700 hover:text-blue-600 transition-colors">
                  <FaUser className="w-5 h-5" />
                </Link>
              ) : (
                <Link to="/login" className="px-3 py-1.5 text-sm bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors">
                  Login
                </Link>
              )}
              
              {/* Mobile Menu Hamburger */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 text-gray-700 hover:text-blue-600 transition-colors"
                aria-label="Toggle mobile menu"
              >
                {isMobileMenuOpen ? (
                  <FaTimes className="w-6 h-6" />
                ) : (
                  <FaBars className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          
          {/* Mobile Menu */}
          <div className="fixed inset-x-0 top-16 bottom-0 bg-white z-50 lg:hidden overflow-y-auto">
          <div className="px-4 py-6 space-y-6">
            {/* Mobile Menu Sections */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <FaGraduationCap className="mr-2 text-blue-600" />
                Learn
              </h3>
              <div className="space-y-2 pl-6">
                <Link 
                  to="/alphabet-learning" 
                  className="block text-gray-700 hover:text-blue-600 transition-colors py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Alphabet Learning
                </Link>
                <Link 
                  to="/pathways" 
                  className="block text-gray-700 hover:text-blue-600 transition-colors py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  All Courses
                </Link>
                <Link 
                  to="/cultural" 
                  className="block text-gray-700 hover:text-blue-600 transition-colors py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Cultural Lessons
                </Link>
                <Link 
                  to="/flashcards" 
                  className="block text-gray-700 hover:text-blue-600 transition-colors py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Flashcards
                </Link>
                <Link 
                  to="/quiz" 
                  className="block text-gray-700 hover:text-blue-600 transition-colors py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Quizzes
                </Link>
                {user && (
                  <>
                    <Link 
                      to="/progress" 
                      className="block text-gray-700 hover:text-blue-600 transition-colors py-2"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Progress Tracker
                    </Link>
                    <Link 
                      to="/word-of-the-day" 
                      className="block text-gray-700 hover:text-blue-600 transition-colors py-2"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Word of the Day
                    </Link>
                  </>
                )}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <FaUsers className="mr-2 text-green-600" />
                Community
              </h3>
              <div className="space-y-2 pl-6">
                <Link 
                  to="/forum" 
                  className="block text-gray-700 hover:text-green-600 transition-colors py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Community Forum
                </Link>
                <Link 
                  to="/teacher-qa" 
                  className="block text-gray-700 hover:text-green-600 transition-colors py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Teacher Q&A
                </Link>
                <Link 
                  to="/teachers" 
                  className="block text-gray-700 hover:text-green-600 transition-colors py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Find Teachers
                </Link>
                <Link 
                  to="/events" 
                  className="block text-gray-700 hover:text-green-600 transition-colors py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Events & Meetups
                </Link>
              </div>
            </div>

            {/* Mobile Auth Section */}
            <div className="border-t border-gray-200 pt-6">
              {user ? (
                <div className="space-y-3">
                  <Link 
                    to="/dashboard" 
                    className="flex items-center text-gray-700 hover:text-blue-600 transition-colors py-2"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <FaTachometerAlt className="mr-3 w-5 h-5" />
                    Dashboard
                  </Link>
                  <Link 
                    to="/profile" 
                    className="flex items-center text-gray-700 hover:text-blue-600 transition-colors py-2"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <FaUser className="mr-3 w-5 h-5" />
                    My Profile
                  </Link>
                  <Link 
                    to="/subscription" 
                    className="flex items-center text-gray-700 hover:text-yellow-600 transition-colors py-2"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <FaCrown className="mr-3 w-5 h-5" />
                    Subscription
                  </Link>
                  <button 
                    onClick={() => {
                      logout();
                      setIsMobileMenuOpen(false);
                    }}
                    className="flex items-center text-gray-700 hover:text-red-600 transition-colors py-2 w-full text-left"
                  >
                    <FaSignOutAlt className="mr-3 w-5 h-5" />
                    Log Out
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <Link 
                    to="/login" 
                    className="flex items-center justify-center px-4 py-3 border border-blue-600 text-blue-600 rounded-lg font-medium hover:bg-blue-50 transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <FaSignInAlt className="mr-2" />
                    Log In
                  </Link>
                  <Link 
                    to="/register" 
                    className="flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <FaUserPlus className="mr-2" />
                    Sign Up
                  </Link>
                </div>
              )}
            </div>

            {/* Mobile Support Links */}
            <div className="border-t border-gray-200 pt-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <Link 
                  to="/help" 
                  className="text-gray-600 hover:text-blue-600 transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Help Center
                </Link>
                <Link 
                  to="/faq" 
                  className="text-gray-600 hover:text-blue-600 transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  FAQ
                </Link>
                <Link 
                  to="/contact" 
                  className="text-gray-600 hover:text-blue-600 transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Contact
                </Link>
                <Link 
                  to="/pricing" 
                  className="text-gray-600 hover:text-blue-600 transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Pricing
                </Link>
              </div>
            </div>
          </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MegaMenu; 